// src/components/purchase/PurchasePanel.jsx
import React, { useState } from "react";
import { Grid3x3, MousePointer2, Square, Trash2, ShoppingCart } from "lucide-react";
import { usePurchaseStore } from "../../stores/usePurchaseStore";

const GRIDS = [
  { id: "us48", label: "Imagery grid", hint: "One-mile cells, the grid imagery is sold in" },
  { id: "glo_ols",  label: "TX Survey", hint: "Texas GLO survey sections" },
];

const TOOLS = [
  { id: "click", label: "Click",   icon: MousePointer2 },
  { id: "box",   label: "Box",     icon: Square },
];

export default function PurchasePanel() {
  const activeGrid = usePurchaseStore(s => s.activeGrid);
  const setActiveGrid = usePurchaseStore(s => s.setActiveGrid);
  const tool = usePurchaseStore(s => s.tool);
  const setTool = usePurchaseStore(s => s.setTool);
  const selected = usePurchaseStore(s => s.selected);
  const clear = usePurchaseStore(s => s.clear);
  const totals = usePurchaseStore(s => s.totals)();

  const [pendingGrid, setPendingGrid] = useState(null);

  function requestGridChange(next) {
    if (next === activeGrid) return;
    if (selected.size > 0) { setPendingGrid(next); return; }
    setActiveGrid(next);
  }

  function confirmGridChange() {
    if (pendingGrid) setActiveGrid(pendingGrid);
    setPendingGrid(null);
  }

  return (
    <div className="h-full flex flex-col text-gray-200">
      {/* Header */}
      <div className="p-3 border-b border-slate-800">
        <div className="text-xs font-mono uppercase tracking-wider text-gray-400 flex items-center gap-2">
          <ShoppingCart size={12} />
          Purchase Imagery
        </div>
        <p className="text-[10px] text-fg-secondary mt-0.5">
          Select grid blocks to build an imagery order.
        </p>
      </div>

      {/* Grid toggle */}
      <Section title="Grid">
        <div className="flex gap-1 bg-bg border border-slate-700 rounded-md p-0.5">
          {GRIDS.map(g => {
            const active = g.id === activeGrid;
            return (
              <button
                key={g.id}
                onClick={() => requestGridChange(g.id)}
                className={`flex-1 px-2 py-1.5 text-[11px] rounded-sm transition ${
                  active
                    ? "text-accent-action border border-accent-action/40"
                    : "text-gray-400 hover:text-gray-200"
                }`}
              >
                <div className="flex items-center justify-center gap-1.5">
                  <Grid3x3 className="w-3 h-3" />
                  {g.label}
                </div>
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-fg-secondary mt-1.5">
          {GRIDS.find(g => g.id === activeGrid)?.hint}
        </p>
      </Section>

      {/* Tool toggle */}
      <Section title="Tool">
        <div className="flex gap-1 bg-bg border border-slate-700 rounded-md p-0.5">
          {TOOLS.map(t => {
            const Icon = t.icon;
            const active = t.id === tool;
            return (
              <button
                key={t.id}
                onClick={() => setTool(t.id)}
                className={`flex-1 px-2 py-1.5 text-[11px] rounded-sm transition ${
                  active
                    ? "text-accent-action border border-accent-action/40"
                    : "text-gray-400 hover:text-gray-200"
                }`}
              >
                <div className="flex items-center justify-center gap-1.5">
                  <Icon className="w-3 h-3" />
                  {t.label}
                </div>
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-fg-secondary mt-1.5">
          {tool === "click"
            ? "Click a block to add or remove it."
            : "Hold shift and drag a box to add visible blocks."}
        </p>
      </Section>

      {/* Legend */}
      <Section title="Legend">
        <div className="flex flex-col gap-2">
          <LegendItem
            color="#22c55e"
            opacity={0.25}
            borderColor="#22c55e"
            borderStyle="solid"
            label="Purchased"
            hint="Your org already owns imagery here"
          />
          <LegendItem
            color="#1f9dff"
            opacity={0.06}
            borderColor="#60a5fa"
            borderStyle="dashed"
            label="Available"
            hint="Imagery exists — click to add to cart"
          />
          <LegendItem
            color="#1f9dff"
            opacity={0.4}
            borderColor="#1f9dff"
            borderStyle="solid"
            label="In Cart"
            hint="Selected for purchase"
          />
          <LegendItem
            color="transparent"
            opacity={0}
            borderColor="transparent"
            borderStyle="none"
            label="No coverage"
            hint="No imagery available — blocks hidden"
            empty
          />
        </div>
      </Section>

      {/* Totals */}
      <Section title="Selection">
        <Row label="Blocks"     value={totals.blocks.toLocaleString()} />
        <Row label="Area"       value={`${totals.sqMi.toFixed(2)} sq mi`} />
        <Row
          label="Est. price"
          value={totals.price == null ? "—" : `$${totals.price.toFixed(2)}`}
          hint="Pricing coming soon"
        />
      </Section>

      <div className="flex-1" />

      {/* Actions */}
      <div className="p-3 border-t border-slate-800 flex gap-2">
        <button
          onClick={clear}
          disabled={totals.blocks === 0}
          className={`flex items-center gap-1 px-2.5 py-1.5 text-[11px] rounded-md border transition ${
            totals.blocks === 0
              ? "border-slate-800 text-slate-600 cursor-not-allowed"
              : "border-slate-700 text-slate-300 hover:text-red-400 hover:border-red-400/40"
          }`}
        >
          <Trash2 size={11} /> Clear
        </button>
        {/* LIGHTS UP WITH A NON-EMPTY CART, AND STILL DOES NOTHING.
            Checkout is not built. The button is kept `disabled` and keeps a
            title saying so, because a lit control that silently no-ops on
            click is worse than a dim one -- the glow is meant to say "you have
            selected something", not "this will work now". When the flow lands,
            the disabled attribute and the title come off together. */}
        <button
          disabled
          title={totals.blocks === 0 ? "Select blocks to purchase" : "Checkout coming soon"}
          className={`flex-1 px-3 py-1.5 text-[11px] rounded-md border transition cursor-not-allowed ${
            totals.blocks === 0
              ? "bg-accent-action/10 border-accent-action/20 text-accent-action/40"
              : "bg-accent-action/25 border-accent-action/70 text-accent-action font-medium"
          }`}
        >
          Checkout
        </button>
      </div>

      {/* Grid-change confirm */}
      {pendingGrid && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && setPendingGrid(null)}
        >
          <div className="w-full max-w-sm bg-bg-soft border border-slate-700 rounded-xl shadow-2xl p-5">
            <h3 className="text-sm font-semibold text-gray-100">Switch grid?</h3>
            <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">
              Switching grids will clear your current selection of{" "}
              <span className="font-semibold text-gray-100">
                {selected.size} block{selected.size === 1 ? "" : "s"}
              </span>.
            </p>
            <div className="flex gap-2 mt-4 justify-end">
              <button
                onClick={() => setPendingGrid(null)}
                className="px-3 py-1.5 text-xs rounded-md border border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={confirmGridChange}
                className="px-3 py-1.5 text-xs rounded-md bg-accent-action/20 border border-accent-action/50 text-accent-action hover:bg-accent-action/30"
              >
                Switch & clear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Legend Item ──────────────────────────────────────────────────────── */

function LegendItem({ color, opacity, borderColor, borderStyle, label, hint, empty }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="mt-0.5 shrink-0">
        {empty ? (
          <div
            className="w-5 h-3.5 rounded-sm"
            style={{
              background: "repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(148,163,184,0.15) 2px, rgba(148,163,184,0.15) 4px)",
              border: "1px solid rgba(148,163,184,0.2)",
            }}
          />
        ) : (
          <div
            className="w-5 h-3.5 rounded-sm"
            style={{
              backgroundColor: color,
              opacity: Math.max(opacity, 0.3),
              border: `1.5px ${borderStyle} ${borderColor}`,
            }}
          />
        )}
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-medium text-gray-200 leading-tight">{label}</div>
        <div className="text-[10px] text-fg-secondary leading-tight mt-0.5">{hint}</div>
      </div>
    </div>
  );
}

/* ── Helpers ──────────────────────────────────────────────────────────── */

function Section({ title, children }) {
  return (
    <div className="px-4 py-3 border-b border-slate-800">
      <div className="text-[10px] font-mono uppercase tracking-wider text-fg-secondary mb-2">{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value, hint }) {
  return (
    <div className="flex items-baseline justify-between py-0.5">
      <span className="text-[11px] text-slate-400">{label}</span>
      <span className="text-[12px] font-medium text-gray-100 tabular-nums" title={hint}>
        {value}
      </span>
    </div>
  );
}
